import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAutomationClient } from '../providers/AutomationProvider'
import type {
  AutomationsListParams,
  AutomationStatsParams,
  CreateAutomationParams,
  UpdateAutomationParams,
  AutomationStatus,
} from '../types'

/**
 * Query key factory for automations-related queries
 */
export const automationsKeys = {
  all: ['automations'] as const,
  lists: () => [...automationsKeys.all, 'list'] as const,
  list: (params?: AutomationsListParams) => [...automationsKeys.lists(), params] as const,
  details: () => [...automationsKeys.all, 'detail'] as const,
  detail: (id: string) => [...automationsKeys.details(), id] as const,
  stats: (id: string, params?: AutomationStatsParams) =>
    [...automationsKeys.detail(id), 'stats', params] as const,
}

/**
 * Hook to fetch a paginated list of automations with stats
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAutomations({ status: 'ACTIVE', limit: 20 })
 * console.log(data?.automations)
 * ```
 */
export function useAutomations(params?: AutomationsListParams) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: automationsKeys.list(params),
    queryFn: () => client.automations.list(params),
  })
}

/**
 * Hook to fetch a single automation with full details
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useAutomation('automation-123')
 * console.log(data?.name, data?.stats)
 * ```
 */
export function useAutomation(automationId: string, options?: { enabled?: boolean }) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: automationsKeys.detail(automationId),
    queryFn: () => client.automations.get(automationId),
    enabled: options?.enabled !== false && !!automationId,
  })
}

/**
 * Hook to fetch detailed stats for a specific automation
 *
 * @example
 * ```tsx
 * const { data } = useAutomationStats('automation-123', { startDate: '2024-01-01' })
 * console.log(data?.runs.total, data?.errors)
 * ```
 */
export function useAutomationStats(
  automationId: string,
  params?: AutomationStatsParams,
  options?: { enabled?: boolean }
) {
  const { client } = useAutomationClient()

  return useQuery({
    queryKey: automationsKeys.stats(automationId, params),
    queryFn: () => client.automations.stats(automationId, params),
    enabled: options?.enabled !== false && !!automationId,
  })
}

/**
 * Hook to create a new automation
 *
 * @example
 * ```tsx
 * const { mutate, isLoading } = useCreateAutomation()
 * mutate({ name: 'New Automation', triggerType: 'WEBHOOK' })
 * ```
 */
export function useCreateAutomation() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateAutomationParams) => client.automations.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationsKeys.lists() })
    },
  })
}

/**
 * Hook to update an automation
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateAutomation()
 * mutate({ id: 'automation-123', data: { name: 'Updated Name' } })
 * ```
 */
export function useUpdateAutomation() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAutomationParams }) =>
      client.automations.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: automationsKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: automationsKeys.lists() })
    },
  })
}

/**
 * Hook to delete an automation
 *
 * @example
 * ```tsx
 * const { mutate } = useDeleteAutomation()
 * mutate('automation-123')
 * ```
 */
export function useDeleteAutomation() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => client.automations.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationsKeys.lists() })
    },
  })
}

/**
 * Hook to manually trigger an automation
 *
 * @example
 * ```tsx
 * const { mutate, data } = useTriggerAutomation()
 * mutate({ id: 'automation-123', payload: { key: 'value' } })
 * console.log(data?.runId)
 * ```
 */
export function useTriggerAutomation() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload?: Record<string, unknown> }) =>
      client.automations.trigger(id, payload),
    onSuccess: (_, variables) => {
      // Invalidate automation to refresh lastRunAt
      queryClient.invalidateQueries({ queryKey: automationsKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: automationsKeys.lists() })
    },
  })
}

/**
 * Hook to update automation status (pause/resume)
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateAutomationStatus()
 * mutate({ id: 'automation-123', status: 'PAUSED' })
 * ```
 */
export function useUpdateAutomationStatus() {
  const { client } = useAutomationClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AutomationStatus }) =>
      client.automations.updateStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: automationsKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: automationsKeys.lists() })
    },
  })
}
